import { ICommandBarItemProps } from 'office-ui-fabric-react/lib/CommandBar';
import { IIconProps } from 'office-ui-fabric-react/lib/Icon';
import { IRenderFunction } from '@uifabric/utilities';
import { ShouldShowBehavior } from './ShouldShowBehavior';
import {
    IContextualMenuItem,
    IContextualMenuProps,
    IContextualMenuItemProps,
} from 'office-ui-fabric-react/lib/ContextualMenu';

export const enum DisplayOption {
    Left,
    Right,
    Overflow,
}

export interface FilterableCommandBarItem extends ICommandBarItemProps {
    shouldShow?: (displayOption?: DisplayOption) => boolean;
}

export default class FilterableMenuItem implements FilterableCommandBarItem {
    public className?: string;
    public disabled?: boolean;
    public iconProps: IIconProps;
    public subMenuProps?: IContextualMenuProps;
    public key: string;
    public name: string;
    public cacheKey: string;
    public onClick: (ev?: any, item?: IContextualMenuItem) => void;
    public onRender?: (
        item: any,
        dismissMenu: (ev?: any, dismissAll?: boolean) => void
    ) => React.ReactNode;
    public checked?: boolean; // Note: canCheck must be true for this to take effect.
    public ariaLabel?: string;
    public title?: string;
    public canCheck?: boolean;
    public iconOnly?: boolean;
    public onRenderIcon?: IRenderFunction<IContextualMenuItemProps>;
    public href?: string;
    public target?: string;
    public split?: boolean;

    private displayOption: DisplayOption;
    private shouldShowBehavior: ShouldShowBehavior;

    constructor(
        name: string,
        icon: string,
        onClick: (ev?: any, item?: IContextualMenuItem) => void,
        shouldShow?: ShouldShowBehavior,
        displayOption?: DisplayOption,
        disabled?: boolean,
        menuContent?: IContextualMenuItem[] | IContextualMenuProps,
        key?: string,
        onRender?: (
            item: any,
            dismissMenu: (ev?: any, dismissAll?: boolean) => void
        ) => React.ReactNode,
        checked?: boolean,
        ariaLabel?: string,
        title?: string,
        className?: string,
        canCheck?: boolean,
        iconOnly?: boolean,
        onRenderIcon?: IRenderFunction<IContextualMenuItemProps>,
        href?: string,
        cacheKey?: string,
        split?: boolean
    ) {
        this.iconProps = icon ? { iconName: icon } : null;
        this.disabled = disabled;
        this.key = key || (name || '') + (icon || '');
        this.name = name;
        this.onClick = onClick;
        this.shouldShowBehavior = shouldShow || this.alwaysShow;
        this.displayOption = displayOption || DisplayOption.Left;
        this.onRender = onRender;
        this.checked = checked;
        this.ariaLabel = ariaLabel;
        this.title = title;
        this.className = className;
        this.canCheck = canCheck;
        this.iconOnly = iconOnly;
        this.onRenderIcon = onRenderIcon;
        this.href = href;
        this.split = split;

        if (cacheKey) {
            this.cacheKey = cacheKey;
        }

        if (this.href) {
            this.target = '_blank';
        }

        // Add the menuContent as a list of items or as context menu props.
        if (menuContent) {
            if (Array.isArray(menuContent)) {
                this.subMenuProps = { items: menuContent, gapSpace: 0 };
            } else {
                this.subMenuProps = menuContent;
            }
        }
    }

    public shouldShow(displayOption?: DisplayOption): boolean {
        if (displayOption != undefined) {
            // Command bar menu items require the specified display option
            return this.displayOption == displayOption && this.shouldShowBehavior();
        } else {
            // Otherwise show the menu item based on the specified show behavior
            return this.shouldShowBehavior();
        }
    }

    private alwaysShow(): boolean {
        return true;
    }
}
